import React, { useState, useMemo } from 'react';
import { Loan } from '../types';
import { RowActions, RowActionCell } from './RowActions';
import {
  PageHeader, SectionHeader, EmptyState, FormScreen, useFormScreenScroll,
  Button, TextButton, Chip, Tag, List, Field, Input, Select, TYPE, cn,
} from './ui';

type LoanDraft = {
  name: string; balance: number; interestRate: number; monthlyPayment: number;
  originalAmount: number; dueDay: number; startDate: string; maturityDate: string;
  loanType: 'personal' | 'auto' | 'student' | 'mortgage' | 'other';
  isActive: boolean;
};

/** One field set for add and edit. */
const LoanFields: React.FC<{
  value: LoanDraft;
  onChange: (next: LoanDraft) => void;
  loanTypes: string[];
  formatLoanType: (t: string) => string;
  idPrefix: string;
}> = ({ value, onChange, loanTypes, formatLoanType, idPrefix }) => {
  const num = (v: string, fallback: number) => (v === '' ? fallback : parseFloat(v) || fallback);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
      <Field label="Loan name" htmlFor={`${idPrefix}-name`}>
        <Input id={`${idPrefix}-name`} type="text" required value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })} />
      </Field>
      <Field label="Loan type" htmlFor={`${idPrefix}-type`}>
        <Select id={`${idPrefix}-type`} value={value.loanType}
          onChange={(e) => onChange({ ...value, loanType: e.target.value as LoanDraft['loanType'] })}>
          {loanTypes.map(t => <option key={t} value={t}>{formatLoanType(t)}</option>)}
        </Select>
      </Field>
      <Field label="Current balance" htmlFor={`${idPrefix}-balance`}>
        <Input id={`${idPrefix}-balance`} type="number" required step="0.01" value={value.balance}
          onChange={(e) => onChange({ ...value, balance: num(e.target.value, 0) })} />
      </Field>
      <Field label="Original amount" htmlFor={`${idPrefix}-original`}>
        <Input id={`${idPrefix}-original`} type="number" required step="0.01" value={value.originalAmount}
          onChange={(e) => onChange({ ...value, originalAmount: num(e.target.value, 0) })} />
      </Field>
      <Field label="Monthly payment" htmlFor={`${idPrefix}-payment`}>
        <Input id={`${idPrefix}-payment`} type="number" required step="0.01" value={value.monthlyPayment}
          onChange={(e) => onChange({ ...value, monthlyPayment: num(e.target.value, 0) })} />
      </Field>
      <Field label="Interest rate" hint="Annual percentage rate." htmlFor={`${idPrefix}-apr`}>
        <Input id={`${idPrefix}-apr`} type="number" required step="0.01" value={value.interestRate}
          onChange={(e) => onChange({ ...value, interestRate: num(e.target.value, 0) })} />
      </Field>
      <Field label="Due day" hint="Day of the month." htmlFor={`${idPrefix}-dueday`}>
        <Input id={`${idPrefix}-dueday`} type="number" min="1" max="31" value={value.dueDay}
          onChange={(e) => onChange({ ...value, dueDay: Math.round(num(e.target.value, 1)) })} />
      </Field>
      <Field label="Start date" hint="Optional." htmlFor={`${idPrefix}-start`}>
        <Input id={`${idPrefix}-start`} type="date" value={value.startDate}
          onChange={(e) => onChange({ ...value, startDate: e.target.value })} />
      </Field>
      <Field label="Maturity date" hint="Optional — when the loan is due to be cleared." htmlFor={`${idPrefix}-maturity`}>
        <Input id={`${idPrefix}-maturity`} type="date" value={value.maturityDate}
          onChange={(e) => onChange({ ...value, maturityDate: e.target.value })} />
      </Field>
    </div>
  );
};


interface LoanManagerProps {
  loans: Loan[];
  onAddLoan: (loan: Omit<Loan, 'id'>) => void;
  onUpdateLoan: (id: string, loan: Partial<Loan>) => void;
  onDeleteLoan: (id: string) => void;
}

// Helper function to parse date string in local timezone
const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/** Loan / type / APR / paid off / monthly / balance / status. Below md the
 *  middle four drop away. */
const LOAN_COLS =
  'grid grid-cols-[1.7fr_1fr_.9fr] md:grid-cols-loans gap-x-4 items-center';

const LoanCol: React.FC<{ label: string; align?: string; deskOnly?: boolean }> = ({
  label, align = 'text-left', deskOnly,
}) => (
  <div className={cn(TYPE.sectionLabel, 'text-ink-muted', align, deskOnly && 'hidden md:block')}>
    {label}
  </div>
);

export const LoanManager: React.FC<LoanManagerProps> = ({
  loans,
  onAddLoan,
  onUpdateLoan,
  onDeleteLoan
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLoanId, setEditingLoanId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'name' | 'balance' | 'monthlyPayment' | 'interestRate' | 'loanType'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [newLoan, setNewLoan] = useState({
    name: '',
    balance: 0,
    interestRate: 0,
    monthlyPayment: 0,
    originalAmount: 0,
    dueDay: 1,
    startDate: new Date().toISOString().split('T')[0],
    maturityDate: '',
    loanType: 'personal' as 'personal' | 'auto' | 'student' | 'mortgage' | 'other',
    isActive: true
  });

  const [editLoan, setEditLoan] = useState({
    name: '',
    balance: 0,
    interestRate: 0,
    monthlyPayment: 0,
    originalAmount: 0,
    dueDay: 1,
    startDate: new Date().toISOString().split('T')[0],
    maturityDate: '',
    loanType: 'personal' as 'personal' | 'auto' | 'student' | 'mortgage' | 'other',
    isActive: true
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const loanToAdd = {
      ...newLoan,
      startDate: newLoan.startDate ? parseLocalDate(newLoan.startDate) : undefined,
      maturityDate: newLoan.maturityDate ? parseLocalDate(newLoan.maturityDate) : undefined
    };
    onAddLoan(loanToAdd);
    setNewLoan({
      name: '',
      balance: 0,
      interestRate: 0,
      monthlyPayment: 0,
      originalAmount: 0,
      dueDay: 1,
      startDate: new Date().toISOString().split('T')[0],
      maturityDate: '',
      loanType: 'personal',
      isActive: true
    });
    setShowAddForm(false);
  };

  const startEdit = (loan: Loan) => {
    rememberListScroll();
    setEditingLoanId(loan.id);
    setEditLoan({
      name: loan.name,
      balance: loan.balance,
      interestRate: loan.interestRate,
      monthlyPayment: loan.monthlyPayment,
      originalAmount: loan.originalAmount,
      dueDay: loan.dueDay || 1,
      startDate: loan.startDate ? new Date(loan.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      maturityDate: loan.maturityDate ? new Date(loan.maturityDate).toISOString().split('T')[0] : '',
      loanType: loan.loanType,
      isActive: loan.isActive
    });
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingLoanId) {
      const loanToUpdate = {
        ...editLoan,
        startDate: editLoan.startDate ? parseLocalDate(editLoan.startDate) : undefined,
        maturityDate: editLoan.maturityDate ? parseLocalDate(editLoan.maturityDate) : undefined
      };
      onUpdateLoan(editingLoanId, loanToUpdate);
      setEditingLoanId(null);
    }
  };

  const cancelEdit = () => {
    setEditingLoanId(null);
  };

  const handleClone = (loan: Loan) => {
    const clonedLoan = {
      name: `${loan.name} (Copy)`,
      balance: loan.balance,
      interestRate: loan.interestRate,
      monthlyPayment: loan.monthlyPayment,
      originalAmount: loan.originalAmount,
      dueDay: loan.dueDay || 1,
      startDate: new Date(),
      maturityDate: loan.maturityDate ? new Date(loan.maturityDate) : undefined,
      loanType: loan.loanType,
      isActive: true
    };
    onAddLoan(clonedLoan);
  };

  const loanTypes = ['personal', 'auto', 'student', 'mortgage', 'other'];

  // Filter and sort loans
  const filteredAndSortedLoans = useMemo(() => {
    let filtered = loans;

    // Apply filters
    if (filterType) {
      filtered = filtered.filter(loan => loan.loanType === filterType);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(loan =>
        filterStatus === 'active' ? loan.isActive : !loan.isActive
      );
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(loan =>
        loan.name.toLowerCase().includes(searchLower) ||
        loan.loanType.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      // Convert to strings for consistent comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [loans, sortBy, sortOrder, filterType, filterStatus, searchTerm]);

  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };


  const formatLoanType = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const rememberListScroll = useFormScreenScroll(showAddForm || editingLoanId !== null);

  const usd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  const hasFilters = Boolean(filterType || filterStatus !== 'all' || searchTerm);

  // Placed after every hook, so hook order never changes between renders.
  const editingLoan = editingLoanId ? loans.find(l => l.id === editingLoanId) ?? null : null;

  const loanFields = (value: typeof newLoan, onChange: (n: typeof newLoan) => void, prefix: string) => (
    <LoanFields
      value={value}
      onChange={onChange}
      loanTypes={loanTypes}
      formatLoanType={formatLoanType}
      idPrefix={prefix}
    />
  );

  if (showAddForm) {
    return (
      <FormScreen
        backLabel="Back to loans"
        heading="New loan"
        sectionTitle="Details"
        hint="Balance, payment and rate are required. Dates are optional."
        submitLabel="Add loan"
        onSubmit={handleSubmit}
        onCancel={() => setShowAddForm(false)}
      >
        {loanFields(newLoan, setNewLoan, 'new-loan')}
      </FormScreen>
    );
  }

  if (editingLoan) {
    return (
      <FormScreen
        backLabel="Back to loans"
        heading="Edit loan"
        subject={editingLoan.name}
        sectionTitle="Details"
        hint="Balance, payment and rate are required. Dates are optional."
        submitLabel="Save changes"
        onSubmit={handleEdit}
        onCancel={cancelEdit}
      >
        {loanFields(editLoan, setEditLoan, 'edit-loan')}
      </FormScreen>
    );
  }

  return (
    <>
      <PageHeader
        heading="Loan Management"
        subhead="Outstanding balances and what they cost each month."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              rememberListScroll();
              setShowAddForm(true);
            }}
          >
            Add loan
          </Button>
        }
      />

      <section className="mt-14">
        <SectionHeader
          label="Filters"
          note={hasFilters ? `${filteredAndSortedLoans.length} of ${loans.length}` : undefined}
        />

        <div className="mt-5 max-w-[420px]">
          <Field label="Search" htmlFor="loan-search">
            <Input
              id="loan-search"
              type="text"
              placeholder="Name or type"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-2">
          <span className={cn(TYPE.metaTight, 'text-ink-muted mr-1')}>Type</span>
          <Chip active={filterType === ''} onClick={() => setFilterType('')}>All</Chip>
          {loanTypes.map(t => (
            <Chip key={t} active={filterType === t} onClick={() => setFilterType(t)}>
              {formatLoanType(t)}
            </Chip>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2">
          <span className={cn(TYPE.metaTight, 'text-ink-muted mr-1')}>Status</span>
          {(['all', 'active', 'inactive'] as const).map(st => (
            <Chip key={st} active={filterStatus === st} onClick={() => setFilterStatus(st)}>
              {st}
            </Chip>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2">
          <span className={cn(TYPE.metaTight, 'text-ink-muted mr-1')}>Sort</span>
          {(['name', 'balance', 'monthlyPayment', 'interestRate', 'loanType'] as const).map(key => (
            <Chip key={key} active={sortBy === key} onClick={() => handleSort(key)}>
              {key === 'monthlyPayment' ? 'Payment' : key === 'interestRate' ? 'Rate' : key === 'loanType' ? 'Type' : key}
              {sortBy === key ? (sortOrder === 'asc' ? ' \u2191' : ' \u2193') : ''}
            </Chip>
          ))}
          {hasFilters && (
            <TextButton
              className="ml-2"
              onClick={() => {
                setFilterType('');
                setFilterStatus('all');
                setSearchTerm('');
              }}
            >
              Clear filters
            </TextButton>
          )}
        </div>
      </section>

      <section className="mt-11">
        {loans.length === 0 ? (
          <EmptyState>No loans added yet</EmptyState>
        ) : filteredAndSortedLoans.length === 0 ? (
          <EmptyState>No loans match the current filters</EmptyState>
        ) : (
          <List>
            <div className={cn(LOAN_COLS, 'px-4 py-2.5 bg-ground-sunk border-b border-rule-base')}>
              <LoanCol label="Loan" />
              <LoanCol label="Type" deskOnly />
              <LoanCol label="APR" align="text-right" deskOnly />
              <LoanCol label="Paid off" deskOnly />
              <LoanCol label="Monthly" align="text-right" deskOnly />
              <LoanCol label="Balance" align="text-right" />
              <LoanCol label="Status" align="text-right" />
            </div>

            {filteredAndSortedLoans.map(loan => (
              <div
                key={loan.id}
                onClick={() => startEdit(loan)}
                className={cn(
                  LOAN_COLS,
                  'px-4 py-3 border-b border-rule last:border-b-0',
                  'cursor-pointer hover:bg-ground-sunk transition-colors duration-[120ms]',
                  !loan.isActive && 'opacity-60'
                )}
              >
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={e => { e.stopPropagation(); startEdit(loan); }}
                    className={cn(
                      TYPE.rowTitle,
                      'text-ink block max-w-full text-left truncate',
                      'hover:text-accent transition-colors duration-[120ms]'
                    )}
                  >
                    {loan.name}
                  </button>
                </div>

                <div className="hidden md:block text-[13px] text-ink-soft truncate">
                  {formatLoanType(loan.loanType)}
                </div>
                <div className="hidden md:block text-[13px] text-ink-soft text-right tabular-nums">
                  {loan.interestRate}%
                </div>

                {/* How much of the original is gone — the one figure a loan has
                    that a card does not. */}
                <div className="hidden md:flex items-center gap-2.5">
                  <div className="flex-1 h-[5px] bg-ground-sunk">
                    <div
                      className="h-[5px] bg-money-in"
                      style={{
                        width: `${Math.max(0, Math.min(100,
                          loan.originalAmount > 0
                            ? ((loan.originalAmount - loan.balance) / loan.originalAmount) * 100
                            : 0))}%`,
                      }}
                    />
                  </div>
                  <span className={cn(TYPE.meta, 'text-ink-muted w-9 text-right')}>
                    {loan.originalAmount > 0
                      ? `${Math.round(((loan.originalAmount - loan.balance) / loan.originalAmount) * 100)}%`
                      : '—'}
                  </span>
                </div>

                <div className="hidden md:block text-[13px] text-money-out text-right tabular-nums">
                  {usd(loan.monthlyPayment)}
                </div>

                <div className={cn(TYPE.rowFigure, 'text-ink text-right')}>{usd(loan.balance)}</div>

                <RowActionCell className="flex items-center justify-end gap-2">
                  {!loan.isActive && <Tag weight="bone">Paused</Tag>}
                  <RowActions
                    label={loan.name}
                    onEdit={() => startEdit(loan)}
                    onClone={() => handleClone(loan)}
                    onTogglePause={() => onUpdateLoan(loan.id, { isActive: !loan.isActive })}
                    isPaused={!loan.isActive}
                    onDelete={() => onDeleteLoan(loan.id)}
                  />
                </RowActionCell>
              </div>
            ))}

            <div className={cn(LOAN_COLS, 'px-4 py-3.5 rule-total')}>
              <div className={cn(TYPE.sectionLabel, 'text-ink-muted')}>
                {filteredAndSortedLoans.length} loans
              </div>
              <div className="hidden md:block" />
              <div className="hidden md:block" />
              <div className="hidden md:block" />
              <div className={cn(TYPE.rowFigure, 'hidden md:block text-money-out text-right')}>
                {usd(filteredAndSortedLoans.filter(l => l.isActive).reduce((t, l) => t + l.monthlyPayment, 0))}
              </div>
              <div className={cn(TYPE.rowFigure, 'text-ink text-right')}>
                {usd(filteredAndSortedLoans.filter(l => l.isActive).reduce((t, l) => t + l.balance, 0))}
              </div>
              <div className={cn(TYPE.meta, 'text-ink-muted text-right')}>
                {filteredAndSortedLoans.filter(l => !l.isActive).length} paused
              </div>
            </div>
          </List>
        )}
      </section>
    </>
  );
};
